import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowTriangleIcon,
  CalendarIcon,
  CurecallBadge,
  CurecallButton,
  CurecallTooltip,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { SMSModal } from 'src/components/sms-modal/sms-modal.component'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import PatientGenderIcon from 'src/components/patient/patient-gender-icon.component'
import { PatientPhone } from 'src/components/patient/patient-phone.component'
import { DashboardService } from 'src/services/dashboard.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useModals } from 'src/providers/modals.provider'
import { getPatientCivilityAndFullName } from 'src/utils/string-formatter.util'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { useLocale } from 'src/hooks/use-locale'
import {
  EHumanReviewRequestProblem,
  EHumanReviewRequestSubject,
  EHumanReviewTab,
  matchesActiveSubject,
  subjectToTab,
  type THumanReviewDetail,
  type THumanReviewRequest,
} from './human-review-requests.type'
import { useSubjectLabels } from './use-subject-labels'
import { useProblemLabels } from './use-problem-labels'

const useGroupedProblemLabels = (requests: THumanReviewRequest[]): string[] => {
  const { locale } = useLocale()
  const problemLabels = useProblemLabels()

  const byProblem = requests.reduce<Record<string, THumanReviewRequest[]>>((acc, request) => {
    if (!acc[request.problem]) acc[request.problem] = []
    acc[request.problem].push(request)
    return acc
  }, {})

  return (Object.entries(byProblem) as [EHumanReviewRequestProblem, THumanReviewRequest[]][]).map(
    ([problem, groupedRequests]) => {
      const label = problemLabels[problem]

      if (problem === EHumanReviewRequestProblem.MISSING_DOCUMENT) {
        const details = groupedRequests
          .filter((request) => request.Variable)
          .map((request) => getTranslatedLabel(locale, request.Variable!.intlLabel))
        return details.length > 0 ? `${label} : ${details.join(', ')}` : label
      }

      if (problem === EHumanReviewRequestProblem.DECLARED_COMPLICATION) {
        const details = groupedRequests
          .filter((request) => request.PatientMedAlert?.MedAlert)
          .map((request) => request.PatientMedAlert!.MedAlert.label)
        return details.length > 0 ? `${label} : ${details.join(', ')}` : label
      }

      return label
    },
  )
}

const OtherProblemsDetail = ({ requests }: { requests: THumanReviewRequest[] }) => {
  const labels = useGroupedProblemLabels(requests)
  return (
    <ul className="c-m-0 c-pl-4 c-list-disc c-text-sm c-text-grey-700">
      {labels.map((label) => (
        <li key={label}>{label}</li>
      ))}
    </ul>
  )
}

type Props = {
  humanReviewDetail: THumanReviewDetail
  activeSubject: EHumanReviewTab
  onTabChange: (tab: EHumanReviewTab) => void
}

const HumanReviewPatientHeader = ({ humanReviewDetail, activeSubject, onTabChange }: Props) => {
  const { t, i18n } = useTranslation()
  const { project, getMedEventLabelById } = useCurrentProject()
  const { openFollowupModal } = useModals()
  const { patient, patientMedEvent, reviewRequests } = humanReviewDetail
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false)
  const [isDetailExpanded, setIsDetailExpanded] = useState(false)
  const subjectLabels = useSubjectLabels()

  const { data: patientOverview } = useQuery({
    queryKey: ['human-review-patient-overview', patient.id, patientMedEvent.id],
    queryFn: () => DashboardService.getDashboardPatient(project.id, patient.id, patientMedEvent.id),
    enabled: isSmsModalOpen,
  })

  const otherUnresolved = reviewRequests.filter((request) => {
    if (request.resolution) return false
    if (activeSubject === EHumanReviewTab.ALL) return false
    return !matchesActiveSubject(request.subject, activeSubject)
  })

  const otherBySubject = otherUnresolved.reduce<Record<string, THumanReviewRequest[]>>((acc, request) => {
    if (!acc[request.subject]) acc[request.subject] = []
    acc[request.subject].push(request)
    return acc
  }, {})
  const otherSubjectEntries = Object.entries(otherBySubject) as [
    EHumanReviewRequestSubject,
    THumanReviewRequest[],
  ][]

  return (
    <section className="c-flex c-flex-col c-my-2 c-ml-4">
      <div className="c-flex c-items-center c-justify-between c-gap-4">
        <div className="c-flex c-items-center c-gap-2">
          <PatientGenderIcon patient={patient} />
          <div className="c-flex c-flex-col">
            <div className="c-font-bold c-text-primary c-flex c-items-center">
              <CurecallTooltip
                content={t('components.patient-med-event-overview.patient-details')}
                tooltipPosition="bottom"
                isPinOnClickEnabled={false}
              >
                <h2 className="c-text-lg c-p-0 c-m-0 c-cursor-pointer">
                  <LocalizedLink
                    to={`/${project.id}/medical/patients/${patient.id}/${patientMedEvent.PatientFollowupId}`}
                  >
                    {getPatientCivilityAndFullName(t, {
                      gender: patient.gender,
                      firstname: patient.firstName ?? '',
                      lastname: patient.lastName ?? '',
                    })}
                  </LocalizedLink>
                </h2>
              </CurecallTooltip>
              <CurecallTooltip
                content={t('components.patient-med-event-overview.followup-medevents')}
                tooltipPosition="bottom"
                isPinOnClickEnabled={false}
              >
                <CurecallButton
                  ariaLabelText={t('components.patient-med-event-overview.followup-medevents')}
                  iconType={<CalendarIcon />}
                  isGhostButton
                  onClick={() =>
                    openFollowupModal({
                      patientId: patient.id,
                      patientFollowupIdToEdit: patientMedEvent.PatientFollowupId,
                    })
                  }
                  size={ECurecallSize.MEDIUM}
                />
              </CurecallTooltip>
            </div>

            <p className="c-text-sm c-font-light c-text-grey-700 c-m-0">
              {new Date(patientMedEvent.date).toLocaleDateString(i18n.language, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
              {' - '}
              {getMedEventLabelById(patientMedEvent.MedEventId)}
            </p>
          </div>
        </div>

        <CurecallButton
          size={ECurecallSize.SMALL}
          appearance={ECurecallButtonAppearance['SECONDARY-OUTLINED']}
          onClick={() => setIsSmsModalOpen((prev) => !prev)}
        >
          {t('components.patient-med-event-overview.see-send-sms')}
        </CurecallButton>
        {isSmsModalOpen && patientOverview && (
          <SMSModal
            isOpen={true}
            patient={{ id: patient.id, firstname: patient.firstName ?? '', lastname: patient.lastName ?? '' }}
            onToggle={() => setIsSmsModalOpen(false)}
            currentPatientFollowup={patientOverview.patientMedEvent.PatientFollowup}
          />
        )}
      </div>
      <div className="c-flex c-items-center c-text-sm">
        <PatientPhone patient={{ ...patient, phone: patient.phone ?? '' }} showCopyButton />
      </div>
      {otherSubjectEntries.length > 0 && (
        <div className="c-flex c-flex-col c-gap-1 c-mt-2">
          <div className="c-flex c-flex-wrap c-items-center c-gap-1.5">
            <span className="c-text-sm c-text-grey-700">
              {t('pages.dashboard.human-review-requests.problems.other-problems-label')}
            </span>
            {otherSubjectEntries.map(([subject, requests]) => {
              const tab = subjectToTab(subject)
              if (!tab) return null
              return (
                <span
                  key={subject}
                  role="button"
                  tabIndex={0}
                  onClick={() => onTabChange(tab)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onTabChange(tab)
                    }
                  }}
                  className="c-cursor-pointer"
                >
                  <CurecallBadge color={ECurecallBadgeColor.SMOOTH_GREY}>
                    {subjectLabels[subject]} ({requests.length})
                  </CurecallBadge>
                </span>
              )
            })}
            <button
              type="button"
              onClick={() => setIsDetailExpanded((prev) => !prev)}
              className="c-inline-flex c-items-center c-gap-0.5 c-text-sm c-text-primary c-cursor-pointer c-border-none c-bg-transparent c-p-0 hover:c-underline hover:c-text-primary-900"
            >
              {isDetailExpanded
                ? t('pages.dashboard.human-review-requests.problems.hide-detail')
                : t('pages.dashboard.human-review-requests.problems.show-more-detail')}
              <ArrowTriangleIcon className="c-h-3 c-w-3" position={isDetailExpanded ? 'UP' : 'DOWN'} />
            </button>
          </div>
          {isDetailExpanded && <OtherProblemsDetail requests={otherUnresolved} />}
        </div>
      )}
    </section>
  )
}

export default HumanReviewPatientHeader
